import React, { Suspense, lazy } from 'react';
import { FullProject, Task } from '../../types';
import { UndoToast } from '../UndoToast';
import { ErrorBoundary } from '../ErrorBoundary';

const MobileView = lazy(() => import('../MobileView'));
const WelcomeScreen = lazy(() => import('../WelcomeScreen').then(m => ({ default: m.WelcomeScreen })));

export const AppSplashLoader: React.FC<{ subtitle: string }> = ({ subtitle }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex flex-col items-center justify-center gap-4">
    <div className="w-14 h-14 bg-gradient-to-tr from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-500/30 animate-pulse">
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    </div>
    <div className="flex flex-col items-center gap-1">
      <p className="text-sm font-bold text-slate-700 dark:text-slate-200">STRATUM Projektmanagement</p>
      <p className="text-xs text-slate-400">{subtitle}</p>
    </div>
  </div>
);

export const AppWelcomeGate: React.FC = () => (
  <ErrorBoundary fallbackTitle="Willkommensseite konnte nicht geladen werden">
    <Suspense fallback={<div className="h-screen w-screen bg-slate-900 flex items-center justify-center" />}>
      <WelcomeScreen />
    </Suspense>
  </ErrorBoundary>
);

export interface AppMobileGateProps {
  isInitialLoadComplete: boolean;
  allProjects: FullProject[];
  currentProjectId: string;
  setCurrentProjectId: (id: string) => void;
  mobileTasks: Task[];
  handleAddTask: (type?: Task['type'], parentId?: string | number, projectId?: string, name?: string, initialData?: Partial<Task>, options?: { bypassDragGuard?: boolean }) => any;
  updateTask: (id: string | number, updates: Partial<Task>, projectId?: string) => void;
  deleteTask: (id: string | number, projectId?: string) => void;
  activeWorkspace: 'professional' | 'private';
  setActiveWorkspace: (ws: 'professional' | 'private') => void;
  isDarkMode: boolean;
  setIsDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  projectManager: any;
  globalMetadata: any;
  addIdea: any;
}

export const AppMobileGate: React.FC<AppMobileGateProps> = ({
  isInitialLoadComplete,
  allProjects,
  currentProjectId,
  setCurrentProjectId,
  mobileTasks,
  handleAddTask,
  updateTask,
  deleteTask,
  activeWorkspace,
  setActiveWorkspace,
  isDarkMode,
  setIsDarkMode,
  projectManager,
  globalMetadata,
  addIdea,
}) => {
  if (!isInitialLoadComplete) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-slate-900 flex-col gap-4">
        <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
        <span className="text-white font-bold tracking-widest text-xs">MOBILE STARTET...</span>
      </div>
    );
  }

  return (
    <ErrorBoundary fallbackTitle="Fehler in der mobilen Ansicht">
      <Suspense
        fallback={
          <div className="flex h-[100dvh] items-center justify-center bg-slate-900 flex-col gap-4">
            <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
            <span className="text-white font-bold tracking-widest text-xs">MOBILE LÄDT...</span>
          </div>
        }
      >
        <MobileView
          allProjects={allProjects}
          currentProjectId={currentProjectId}
          setCurrentProjectId={setCurrentProjectId}
          tasks={mobileTasks}
          onAddTask={handleAddTask}
          onUpdateTask={updateTask}
          onDeleteTask={deleteTask}
          activeWorkspace={activeWorkspace}
          setActiveWorkspace={setActiveWorkspace}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          projectManager={projectManager}
          globalMetadata={globalMetadata}
          addIdea={addIdea}
        />
      </Suspense>
      <UndoToast />
    </ErrorBoundary>
  );
};
